'use client';

import { GatewayCredentialDialogView } from '@slims/ui/organisms';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

interface GatewayCredentialDialogProps {
	meterId: string;
	meterCodice: string;
	open: boolean;
	onClose: () => void;
	onRegistered?: () => void;
}

/**
 * Registers/rotates a hardware-gateway device secret for a meter. The raw secret
 * is returned by the API exactly once (PUT /api/meters/:id/gateway) and shown
 * here for the operator to copy — it is never recoverable afterwards.
 */
export function GatewayCredentialDialog({
	meterId,
	meterCodice,
	open,
	onClose,
	onRegistered,
}: GatewayCredentialDialogProps) {
	const [deviceId, setDeviceId] = useState('');
	const [secret, setSecret] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [copied, setCopied] = useState(false);
	const orgId = useAdminScopeStore((s) => s.configId);

	if (!open) return null;

	async function handleRegister() {
		if (!deviceId.trim()) {
			toast.error('Inserisci il seriale del dispositivo');
			return;
		}
		setBusy(true);
		try {
			const res = await api.put<{ secret: string }>(
				`/api/meters/${meterId}/gateway?orgId=${orgId}`,
				{ deviceId: deviceId.trim() },
			);
			setSecret(res.secret);
			onRegistered?.();
			toast.success('Gateway registrato - copia il secret ora');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Registrazione fallita');
		} finally {
			setBusy(false);
		}
	}

	async function copySecret() {
		if (!secret) return;
		await navigator.clipboard.writeText(secret);
		setCopied(true);
		setTimeout(() => setCopied(false), 1500);
	}

	function close() {
		setDeviceId('');
		setSecret(null);
		setCopied(false);
		onClose();
	}

	return (
		<GatewayCredentialDialogView
			meterCodice={meterCodice}
			deviceId={deviceId}
			onDeviceIdChange={setDeviceId}
			secret={secret}
			busy={busy}
			copied={copied}
			onRegister={handleRegister}
			onCopySecret={copySecret}
			onClose={close}
		/>
	);
}
